import { eventChannel } from 'redux-saga'
import { put, takeEvery, call, take, select, all } from 'redux-saga/effects'
import { signInWithCustomToken } from 'firebase/auth'
import { ref, child, onValue, off, get, set, onDisconnect, remove as dbRemove, query, orderByChild, limitToLast, limitToFirst, startAt, endAt } from 'firebase/database'
import { initializeFirebase, getFirebaseAuth, getFirebaseDatabase } from './firebase.config'

import { Game, Timer } from '../../Core'
import Api from '../Api'

import * as actions from './firebase.actions'
import * as authActions from '../Auth/auth.actions'
import * as IndicatorsActions from '../../Mars/Indicators/indicators.actions'
import * as loaderActions from '../../Core/Loader/loader.actions'
import * as mapActions from '../../Mars/Map/map.actions'
import * as phasesActions from '../../Mars/Phases/phases.actions'
import * as activityActions from '../../Mars/Activity/activity.actions'
import * as battlesActions from '../../Mars/Battles/battles.actions'
import * as miningActions from '../../Mars/Mining/mining.actions'
import * as endGameScreenActions from '../../Mars/Phases/EndGameScreen/endgame.actions'
import * as quizzesActions from '../../Mars/Quizzes/quizzes.actions'

import * as selectors from './firebase.selectors'
import * as indicatorsSelectors from '../../Mars/Indicators/indicators.selectors'
import * as MapSelectors from '../../Mars/Map/map.selectors'

import { SESSION_START_TIMER_ID } from '../../Mars/Phases/phases.const'
import * as Util from '../../Mars/Util/util'
import { log } from 'three/tsl'

let databaseRef
let firebaseDbPath
let ownPlayerId
let currentTurn

const getFirebaseValue = (uid) =>
  new Promise((resolve, reject) => {
    const childRef = child(databaseRef, uid)
    onValue(childRef, 
      (snapshot) => {
        off(childRef)
        resolve(snapshot.val())
      },
      (err) => reject(err),
      { onlyOnce: true }
    )
  })

const setFirebaseListener = (param, uid, callback, errorCallback) => {
  const childRef = child(databaseRef, uid)
  return onValue(childRef, callback, errorCallback)
}

const eventChannelFirebase = (args) =>
  eventChannel((emitter) => {
    setFirebaseListener(
      args[0],
      args[1],
      (data) => {
        emitter({ data })
      },
      (err) => {
        // Log error but don't emit it to avoid cancelling all sagas
        console.error('Firebase listener error:', err)
        // Instead of emitting error, we could emit a specific error action
        // emitter({ error: err })
      }
    )
    return () => {}
  })

export function* initFirebase() {
  // Initialize the firebase app and request a token to authenticate this player
  const { auth, database } = initializeFirebase(process.env.FIREBASE_CONFIG)
  
  const ret = yield Api.post('/firebase/signIn', {})

  // Sign in into firebase
  const account = yield call(signInWithCustomToken, auth, ret.accessToken)
  firebaseDbPath = ret.firebaseDbPath

  // Get a reference of the firebase database
  databaseRef = ref(database)
  ownPlayerId = account.user.uid
  // Track user connectivity
  const onlineUsersRef = child(databaseRef, `${firebaseDbPath}/ONLINE_USERS/${account.user.uid}`)

  const playerData = yield call(get, child(databaseRef, `${firebaseDbPath}/playersData/${account.user.uid}`))
  const federationsInfo = yield call(get, child(databaseRef, `${firebaseDbPath}/federationsInfo`))

  currentTurn = playerData.val().turn

  yield call(set, onlineUsersRef, { email: account.user.email, uid: account.user.uid, timestamp: Date.now() })
  
  const connectedRef = ref(database, '.info/connected')
  onValue(connectedRef, (snap) => {
    if (snap.val()) {
      const disconnectRef = onDisconnect(onlineUsersRef)
      disconnectRef.remove()
    }
  })
  // Save the data needed to identify this player in firebase: groupId and uid  (the uid is the licenseId)
  yield put(
    actions.firebaseInitialized({
      accessToken: ret.accessToken,
      uid: ret.uid,
      groupId: ret.groupId,
      firebaseDbPath: ret.firebaseDbPath,
      playerData: playerData.val(),
      federationsInfo: federationsInfo.val(),
    })
  )
}

export function* loadActivePlayers() {
  const channel = yield call(eventChannelFirebase, ['value', `${firebaseDbPath}/ONLINE_USERS`])
  try {
    while (true) {
      const { data, err } = yield take(channel)
      if (data && data.val()) {
        yield put(actions.setActivePlayerList(Object.keys(data.val())))
      }
      if (err) {
        console.log('loadActivePlayersError', err)
      }
    }
  } finally {
    // do stuff here
  }
}

export function* loadPlayersList() {
  const channel = yield call(eventChannelFirebase, ['value', `${firebaseDbPath}/playersList`])
  try {
    while (true) {
      const { data, err } = yield take(channel)
      if (data && data.val()) {
        yield put(actions.setPlayerList(data.val()))
        // todo wip
        // yield loadPlayersBySettlers()
      }
      if (err) {
        console.log('loadPlayersListError', err)
      }
    }
  } finally {
    // do stuff here
  }
}

export function* loadPlayersBySettlers(limit = 20, page = 1) {
  try {
    const sort = `-settlersByTurn/${currentTurn - 1}`;
    const players = yield call(fetchPlayers, sort, limit, page)
    return players
  } catch (err) {
    console.log('loadPlayersBySettlers', err)
    return []
  }
}

export function* fetchPlayers(sort = '-colonyName', limit = 20, page = 1, search) {
  try {
    const sortingOrder = sort[0] === '-' ? 'desc' : 'asc'
    const sortingField = sortingOrder === 'desc' ? sort.slice(1) : sort
    const sortingLimit = sortingOrder === 'desc' ? limitToLast : limitToFirst
    const playersRef = child(databaseRef, `${firebaseDbPath}/playersList`)
    const playersQuery = search 
      ? query(playersRef, orderByChild(sortingField), startAt(search), endAt(search + '~'), sortingLimit(limit)) 
      : query(playersRef, orderByChild(sortingField), sortingLimit(limit))
    const snapshot = yield call(get, playersQuery)
    const value = snapshot.val() || {}
    const players = Object.entries(value)
      .map(([id, data]) => ({ 
        id, 
        ...data,
        settlers: data.settlersByTurn?.[currentTurn - 1] || data.settlers // get settlers from settlersByTurn
      }))

    let sortedPlayers = Util.sortBy(players, sortingField, sortingOrder)     
    return sortedPlayers
  } catch (err) {
    console.log('fetchPlayers', err)
    return []
  }
}

export function* loadPlayersData() {
  const channel = yield call(eventChannelFirebase, ['value', `${firebaseDbPath}/playersData`])
  try {
    while (true) {
      const { data, err } = yield take(channel)
      if (data && data.val()) {
        yield put(actions.setPlayersData(data.val()))
      }
      if (err) {
        console.log('loadPlayersDataError', err)
      }
    }
  } finally {
    // do stuff here
  }
}

// todo federations info keep unchanged this is not needed
// export function* loadFederationsInfo() {
//   const channel = yield call(eventChannelFirebase, ['value', `${firebaseDbPath}/federationsInfo`])
//   try {
//     while (true) {
//       const { data, err } = yield take(channel)
//       if (data && data.val()) {
//         yield put(actions.setFederationsInfo(data.val()))
//       }
//       if (err) {
//         console.log('loadFederationsInfoError', err)
//       }
//     }
//   } finally {
//     // do stuff here
//   }
// }

export function* loadMap(playerId, mapTurn) {
  const map = yield getFirebaseValue(`${firebaseDbPath}/maps/${playerId}`)
  mapTurn = mapTurn ? mapTurn : currentTurn
  // force current session map view, needed for players that left playing many sessions ago
  if (playerId !== ownPlayerId) {
    const groupData = yield select(selectors.getGroupData)
    // current session min turn
    const minTurn = groupData.session * groupData.maxTurns - 1
    mapTurn = Math.max(mapTurn, minTurn)
  }
  // building model has changed so parse buildings to adapt to game old model behaviour
  const parsedBuildings = Object.keys(map.buildings).reduce((acc, key) => {
    const parsedBuilding = Util.parseBuilding(map.buildings[key], map.buildings, mapTurn)
    if (parsedBuilding) {
      acc[key] = parsedBuilding
    }
    return acc
  }, {})

  return {
    ...map,
    buildings: parsedBuildings,
  }
}

export function* loadUserData(playerId) {
  return yield getFirebaseValue(`${firebaseDbPath}/playersData/${playerId}`)
}

export function* loadOwnMap() {
  let userMap = yield loadMap(ownPlayerId)
  yield put(mapActions.setUserMap(userMap))
}

function* createListener(
  action_type,
  route,
  listener_type,
  callback,
  storeName = 'userMap',
  getter = 'getUserMap',
  setter = 'setUserMap'
) {
  if (!action_type || !route || !listener_type) {
    return null
  }

  const listener = yield call(eventChannelFirebase, [action_type, route])

  try {
    while (true) {
      const affected_item = yield take(listener)
      if (affected_item) {
        yield put(actions.setListenerType(listener_type, storeName))
        let item = affected_item.data.val()

        // Skip processing if item is null
        if (!item) {
          continue
        }

        let key = item.q ? item.q + '_' + item.r : affected_item.data.key
        let storeItems = yield select(MapSelectors[getter])

        if (storeItems) {
          let data = callback(key, item, storeItems)
          if (data) {
            yield put(mapActions[setter](data))
          }
        }
      }
    }
  } catch (err) {
    console.log(err.message)
  } finally {
    //test
  }
}

function remove(key, item, storeitems, reducerParam = 'buildings') {
  if (storeitems[reducerParam][key]) {
    return key
  }
  return null
}

function update(key, item, storeitems, reducerParam = 'buildings') {
  if (storeitems[reducerParam][key]) {
    return {
      [reducerParam]: {
        ...storeitems[reducerParam],
        [key]: item,
      },
    }
  }
  return null
}

function create(key, item, storeitems, reducerParam = 'buildings') {
  if (!storeitems[reducerParam][key]) {
    return {
      [reducerParam]: {
        ...storeitems[reducerParam],
        [key]: item,
      },
    }
  }
  return null
}

function* handleOwnListeners() {
  if (ownPlayerId) {
    //ACTIONS
    let updateBuildingAction = (key, item, storeitems) =>
      update(key, Util.parseBuilding(item, storeitems.buildings, currentTurn), storeitems, 'buildings')
    let removeBuildingAction = (key, item, storeitems) =>
      remove(key, Util.parseBuilding(item, storeitems.buildings, currentTurn), storeitems, 'buildings')
    let createBuildingAction = (key, item, storeitems) =>
      create(key, Util.parseBuilding(item, storeitems.buildings, currentTurn), storeitems, 'buildings')
    let createRemovedRockAction = (key, item, storeitems) =>
      create(key, item, storeitems, 'removedRocks')
    let updateRemovedRockAction = (key, item, storeitems) =>
      update(key, item, storeitems, 'removedRocks')
    let createExtraBuildingAction = (key, item, storeitems) =>
      create(key, item, storeitems, 'extraBuildings')
    let updateExtraBuildingAction = (key, item, storeitems) =>
      update(key, item, storeitems, 'extraBuildings')

    //PROMISE
    yield all([
      createListener(
        'child_removed',
        `${firebaseDbPath}/maps/${ownPlayerId}/buildings`,
        'BUILDING_DELETE',
        removeBuildingAction,
        'userMap',
        'getUserMap',
        'deleteBuilding'
      ),
      createListener(
        'child_added',
        `${firebaseDbPath}/maps/${ownPlayerId}/buildings`,
        'BUILDING_CREATE',
        createBuildingAction
      ),
      createListener(
        'child_changed',
        `${firebaseDbPath}/maps/${ownPlayerId}/buildings`,
        'BUILDING_UPDATE',
        updateBuildingAction
      ),
      createListener(
        'child_added',
        `${firebaseDbPath}/maps/${ownPlayerId}/removedRocks`,
        'MOUNTAIN_DEMOLISH',
        createRemovedRockAction
      ),
      createListener(
        'child_changed',
        `${firebaseDbPath}/maps/${ownPlayerId}/removedRocks`,
        'MOUNTAIN_UPDATED',
        updateRemovedRockAction
      ),
      createListener(
        'child_added',
        `${firebaseDbPath}/maps/${ownPlayerId}/extraBuildings`,
        'EXTRA_BUILDING_CREATE',
        createExtraBuildingAction
      ),
      createListener(
        'child_changed',
        `${firebaseDbPath}/maps/${ownPlayerId}/extraBuildings`,
        'EXTRA_BUILDING_UPDATE',
        updateExtraBuildingAction
      ),
    ])
  }
}

// todo es realmente necesario mantener listeners sobre los mapas visitados????
function* handleVisitorListeners(action) {
  let visitor = action.data
  let id = visitor.visitorId

  if (id) {
    //ACTIONS
    let updateBuildingAction = (key, item, storeitems) =>
      update(key, Util.parseBuilding(item, storeitems.buildings, currentTurn), storeitems, 'buildings')
    let removeBuildingAction = (key, item, storeitems) =>
      remove(key, Util.parseBuilding(item, storeitems.buildings, currentTurn), storeitems, 'buildings')
    let createBuildingAction = (key, item, storeitems) =>
      create(key, Util.parseBuilding(item, storeitems.buildings, currentTurn), storeitems, 'buildings')
    let createRemovedRockAction = (key, item, storeitems) =>
      create(key, item, storeitems, 'removedRocks')
    let updateRemovedRockAction = (key, item, storeitems) =>
      update(key, item, storeitems, 'removedRocks')

    //PROMISE
    yield all([
      createListener(
        'child_removed',
        `${firebaseDbPath}/maps/${id}/buildings`,
        'BUILDING_DELETE',
        removeBuildingAction,
        'visitorMap',
        'getVisitorMap',
        'deleteVisitorBuilding'
      ),
      createListener(
        'child_changed',
        `${firebaseDbPath}/maps/${id}/buildings`,
        'BUILDING_UPDATE',
        updateBuildingAction,
        'visitorMap',
        'getVisitorMap',
        'setVisitorMap'
      ),
      createListener(
        'child_added',
        `${firebaseDbPath}/maps/${id}/buildings`,
        'BUILDING_CREATE',
        createBuildingAction,
        'visitorMap',
        'getVisitorMap',
        'setVisitorMap'
      ),
      createListener(
        'child_added',
        `${firebaseDbPath}/maps/${id}/removedRocks`,
        'MOUNTAIN_DEMOLISH',
        createRemovedRockAction,
        'visitorMap',
        'getVisitorMap',
        'setVisitorMap'
      ),
      createListener(
        'child_changed',
        `${firebaseDbPath}/maps/${id}/removedRocks`,
        'MOUNTAIN_UPDATED',
        updateRemovedRockAction,
        'visitorMap',
        'getVisitorMap',
        'setVisitorMap'
      ),
    ])
  }
}

export function* sessionListener() {
  const channel = yield call(eventChannelFirebase, ['value', `${firebaseDbPath}/config`])
  try {
    while (true) {
      const { data, err } = yield take(channel)
      if (data && data.val()) {
        const { blocked, session, endGame, startSessionDate } = data.val()
        const groupData = yield select(selectors.getGroupData)
        const now = new Date()
        const startDate = new Date(startSessionDate)

        if (endGame) {
          //Here-> action GOTO_ENDGAME_SCREEN when it
          yield put(loaderActions.addLoader('changing_session_loader'))
          yield put(activityActions.loadActivityData())
          yield put(quizzesActions.getQuestionsList())
          yield put(battlesActions.getBattle())
          yield put(miningActions.getOwnQuestions())
          yield put(miningActions.getCountDisputes())
          yield put(phasesActions.goToPhase(3))
          yield put(endGameScreenActions.loadData())
          // yield put (Game.actions.addScreen('end_game'))
          yield put(loaderActions.removeLoader('changing_session_loader'))
        } else if (startDate > now) {
          yield put(loaderActions.addLoader('changing_session_loader'))
          yield put(
            Timer.actions.setExpirationSeconds(SESSION_START_TIMER_ID, (startDate - now) / 1000)
          )
          yield put(Timer.actions.start(SESSION_START_TIMER_ID))
          yield put(Game.actions.addPopup('waiting_session_start_popup'))
        } else if (blocked !== groupData.blocked) {
          // if blocked changes we swap loading screen
          if (blocked) {
            yield put(loaderActions.addLoader('changing_session_loader'))
          } else {
            yield put(activityActions.loadActivityData())
            yield put(loaderActions.removeLoader('changing_session_loader'))
            yield put(battlesActions.setIndividualBattle(null))
            yield put(mapActions.clearSprites())
            if (session !== groupData.session) {
              yield put(battlesActions.getBattle())
              yield put(Game.actions.addPopup('new_session_popup'))
            }
          }
        }
        yield put(actions.setGroupData(data.val()))
      }
      if (err) {
        console.log('sessionListenerError', err)
      }
    }
  } finally {
    // do stuff here
  }
}

export function* playerDataListener() {
  const channel = yield call(eventChannelFirebase, [
    'value',
    `${firebaseDbPath}/playersData/${ownPlayerId}`,
  ])
  try {
    while (true) {
      const { data, err } = yield take(channel)
      if (data && data.val()) {
        const userData = data.val()
        const { phase, turn } = userData
        const { phase: currentPhase } = yield select(indicatorsSelectors.getUserData)
        currentTurn = turn

        yield loadOwnMap(ownPlayerId)

        if (currentPhase && phase !== currentPhase) {
          yield put(phasesActions.goToPhase(phase))
        }
        yield put(IndicatorsActions.setUserData(userData))
        yield put(actions.setUserData(userData))
      }
      if (err) {
        console.log('playerDataListenerError', err)
      }
    }
  } finally {
    // do stuff here
  }
}

/**
 *  Firebase sagas entrypoint
 */
export function* sagas() {
  yield all([
    takeEvery(authActions.LOGIN_FINISH_SUCCESS, initFirebase),
    takeEvery(actions.FIREBASE_INITIALIZED, playerDataListener),
    takeEvery(actions.FIREBASE_INITIALIZED, loadActivePlayers),
    takeEvery(actions.FIREBASE_INITIALIZED, loadPlayersList),
    takeEvery(actions.FIREBASE_INITIALIZED, loadPlayersData),
    // todo federation info keep unchanged this is not needed
    // takeEvery(actions.FIREBASE_INITIALIZED, loadFederationsInfo),
    takeEvery(actions.FIREBASE_INITIALIZED, loadOwnMap),
    takeEvery(actions.FIREBASE_INITIALIZED, sessionListener),
    takeEvery(mapActions.MAP_LOADED, handleOwnListeners),
    takeEvery(mapActions.SET_VISITOR_MAP, handleVisitorListeners),
  ])
}
